import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  Users,
  Send,
  BarChart3,
  Target,
  CheckCircle,
  MessageSquare,
  Zap,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

const BulkSmsSection = () => {
  const navigate = useNavigate();
  const [windowWidth, setWindowWidth] = useState(1024);

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const features = [
    {
      icon: Users,
      title: "Mass Distribution",
      description:
        "Send to thousands of recipients simultaneously with our bulk SMS platform",
    },
    {
      icon: Target,
      title: "Smart Targeting",
      description:
        "Advanced audience segmentation and personalized messaging campaigns",
    },
    {
      icon: BarChart3,
      title: "Analytics & Reports",
      description:
        "Comprehensive delivery reports and campaign performance analytics",
    },
    {
      icon: CheckCircle,
      title: "High Delivery Rate",
      description: "99.9% delivery success rate with real-time status tracking",
    },
  ];

  // Responsive values based on screen size
  const getResponsiveValues = () => {
    if (windowWidth < 640) {
      return { phoneCount: 8, radius: 140, waveSize: 60 };
    } else if (windowWidth < 768) {
      return { phoneCount: 12, radius: 180, waveSize: 80 };
    } else if (windowWidth < 1024) {
      return { phoneCount: 16, radius: 220, waveSize: 100 };
    } else {
      return { phoneCount: 16, radius: 280, waveSize: 120 };
    }
  };

  const { phoneCount, radius, waveSize } = getResponsiveValues();

  return (
    <section className="overflow-hidden py-20 bg-gradient-to-br from-gray-900 via-blue-900 to-gray-900">
      <div className="px-4 mx-auto max-w-8xl">
        {/* Header */}
        <motion.div
          initial={{ y: 50, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.8 }}
          className="mb-16 text-center"
        >
          <h2 className="mb-6 text-4xl font-bold text-white md:text-5xl">
            Bulk SMS for
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-red-400">
              Mass Communication
            </span>
          </h2>
          <p className="mx-auto max-w-3xl text-xl text-gray-300">
            Reach thousands of customers instantly with our powerful bulk SMS
            platform
          </p>
        </motion.div>

        <div className="grid grid-cols-1 gap-8 items-center lg:gap-16 lg:grid-cols-2">
          {/* Content */}
          <motion.div
            initial={{ x: -100, opacity: 0 }}
            whileInView={{ x: 0, opacity: 1 }}
            transition={{ duration: 0.8 }}
            className="order-2 space-y-6 lg:space-y-8 lg:order-1"
          >
            <div className="space-y-4">
              <motion.div
                initial={{ scale: 0 }}
                whileInView={{ scale: 1 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-blue-800 bg-blue-100 rounded-full"
              >
                <Send className="mr-2 w-4 h-4" />
                Bulk SMS
              </motion.div>

              <motion.h3
                initial={{ y: 50, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.8, delay: 0.3 }}
                className="text-3xl font-bold leading-tight text-white md:text-4xl"
              >
                Scale Your
                <span className="block text-blue-400">Messaging Campaigns</span>
              </motion.h3>

              <motion.p
                initial={{ y: 50, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.8, delay: 0.4 }}
                className="text-lg leading-relaxed text-gray-300"
              >
                Perfect for marketing campaigns, announcements, and mass
                notifications. Our bulk SMS service delivers your messages to
                thousands of recipients with advanced targeting and
                comprehensive analytics.
              </motion.p>
            </div>

            <motion.div
              initial={{ y: 50, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.5 }}
              className="grid grid-cols-1 gap-6 sm:grid-cols-2"
            >
              {features.map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ x: -50, opacity: 0 }}
                  whileInView={{ x: 0, opacity: 1 }}
                  transition={{ duration: 0.6, delay: 0.6 + index * 0.1 }}
                  className="flex items-start space-x-4"
                >
                  <div className="flex flex-shrink-0 justify-center items-center w-12 h-12 rounded-xl bg-blue-500/20">
                    <feature.icon className="w-6 h-6 text-blue-400" />
                  </div>
                  <div>
                    <h4 className="mb-2 text-lg font-semibold text-white">
                      {feature.title}
                    </h4>
                    <p className="text-sm text-gray-300">
                      {feature.description}
                    </p>
                  </div>
                </motion.div>
              ))}
            </motion.div>

            <motion.div
              initial={{ y: 50, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.8 }}
              className="flex flex-col gap-4 sm:flex-row"
            >
              <motion.button
                onClick={() => navigate("/login")}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="px-8 py-4 font-semibold text-white bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl shadow-lg transition-all duration-300 hover:shadow-xl"
              >
                Start Bulk SMS
              </motion.button>
              <motion.button
                onClick={() => navigate("/contact-us")}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="px-8 py-4 font-semibold text-blue-400 rounded-xl border-2 border-blue-500 transition-all duration-300 hover:bg-blue-500/10"
              >
                Contact Us
              </motion.button>
            </motion.div>
          </motion.div>

          {/* Interactive Visualization */}
          <motion.div
            initial={{ x: 100, opacity: 0 }}
            whileInView={{ x: 0, opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="flex order-1 justify-center lg:justify-end lg:order-2"
          >
            <div className="relative w-full max-w-sm sm:max-w-md md:max-w-lg lg:max-w-xl xl:max-w-2xl h-[400px] sm:h-[500px] md:h-[600px] lg:h-[700px] xl:h-[900px] overflow-hidden">
              {/* Central Phone */}
              <div className="absolute top-1/2 left-1/2 z-20 transform -translate-x-1/2 -translate-y-1/2">
                <motion.div
                  animate={{
                    y: [0, -10, 0],
                    rotateY: [0, 5, -5, 0],
                  }}
                  transition={{
                    duration: 4,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                  className="relative"
                >
                  {/* Phone Body */}
                  <div className="w-20 h-40 bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl border-2 border-gray-700 shadow-2xl sm:w-24 sm:h-48 md:w-28 md:h-56 lg:w-32 lg:h-64 sm:rounded-3xl sm:border-4">
                    {/* Screen */}
                    <div className="overflow-hidden relative m-1 sm:m-2 w-[72px] h-32 sm:w-20 sm:h-40 md:w-24 md:h-48 lg:w-28 lg:h-56 bg-black rounded-xl sm:rounded-2xl">
                      {/* Screen Content */}
                      <div className="p-1 h-full bg-gradient-to-br from-red-400 to-blue-500 rounded-xl sm:p-2 lg:p-3 sm:rounded-2xl">
                        {/* SMS Icon */}
                        <div className="flex justify-center mb-1 sm:mb-2 lg:mb-4">
                          <div className="flex justify-center items-center w-4 h-4 bg-white rounded-lg sm:w-6 sm:h-6 lg:w-8 lg:h-8 sm:rounded-xl">
                            <MessageSquare className="w-2 h-2 text-red-500 sm:w-3 sm:h-3 lg:w-5 lg:h-5" />
                          </div>
                        </div>

                        {/* Message Bubbles */}
                        <div className="space-y-1 sm:space-y-2">
                          {[...Array(3)].map((_, i) => (
                            <motion.div
                              key={i}
                              initial={{ opacity: 0, x: -20 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{ delay: i * 0.5, duration: 0.5 }}
                              className={`p-1 sm:p-2 rounded text-[8px] sm:text-xs ${
                                i % 2 === 0
                                  ? "bg-white/90 text-gray-800 ml-1 sm:ml-2"
                                  : "bg-blue-500 text-white mr-1 sm:mr-2"
                              }`}
                            >
                              {i === 0 ? "Bulk SMS" : `SMS ${i + 1}`}
                            </motion.div>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Camera */}
                    <div className="flex absolute top-2 left-1/2 justify-center items-center w-6 h-2 bg-gray-700 rounded-full transform -translate-x-1/2 sm:top-4 sm:w-8 sm:h-3 lg:w-12 lg:h-4">
                      <div className="w-1 h-1 sm:w-1.5 sm:h-1.5 lg:w-2 lg:h-2 bg-blue-400 rounded-full animate-pulse"></div>
                    </div>
                  </div>

                  {/* Signal Waves */}
                  {[...Array(3)].map((_, i) => (
                    <motion.div
                      key={i}
                      className="absolute top-1/2 left-1/2 rounded-full border border-red-400 transform -translate-x-1/2 -translate-y-1/2 sm:border-2"
                      style={{
                        width: `${(i + 1) * waveSize}px`,
                        height: `${(i + 1) * waveSize}px`,
                      }}
                      animate={{
                        scale: [1, 1.2, 1],
                        opacity: [0.8, 0.2, 0.8],
                      }}
                      transition={{
                        duration: 2,
                        repeat: Infinity,
                        delay: i * 0.4,
                        ease: "easeInOut",
                      }}
                    />
                  ))}
                </motion.div>
              </div>

              {/* Recipient Phones Grid */}
              {[...Array(phoneCount)].map((_, i) => {
                const angle = (i / phoneCount) * 2 * Math.PI;
                const x = Math.cos(angle) * radius;
                const y = Math.sin(angle) * radius;

                return (
                  <motion.div
                    key={i}
                    className="absolute"
                    style={{
                      left: `calc(50% + ${x}px)`,
                      top: `calc(50% + ${y}px)`,
                      transform: "translate(-50%, -50%)",
                    }}
                    initial={{ scale: 0, opacity: 0 }}
                    animate={{
                      scale: 1,
                      opacity: 1,
                      y: [0, -5, 0],
                    }}
                    transition={{
                      delay: i * 0.1,
                      duration: 0.5,
                      y: {
                        duration: 2,
                        repeat: Infinity,
                        delay: i * 0.1,
                      },
                    }}
                  >
                    <div className="w-4 h-8 bg-gradient-to-br from-blue-500 to-blue-600 rounded border border-blue-400 shadow-lg sm:w-6 sm:h-12 md:w-8 md:h-16 sm:rounded-lg">
                      <div className="relative m-0.5 sm:m-1 w-3 h-6 sm:w-4 sm:h-8 md:w-6 md:h-12 bg-blue-900 rounded">
                        {/* Success indicator */}
                        <motion.div
                          className="absolute -top-0.5 -right-0.5 sm:-top-1 sm:-right-1 w-2 h-2 sm:w-3 sm:h-3 bg-red-400 rounded-full"
                          animate={{ scale: [1, 1.3, 1] }}
                          transition={{
                            duration: 1,
                            repeat: Infinity,
                            delay: 2 + i * 0.1,
                          }}
                        >
                          <CheckCircle className="w-1 h-1 text-white sm:w-2 sm:h-2" />
                        </motion.div>
                      </div>
                    </div>
                  </motion.div>
                );
              })}

              {/* Message Particles */}
              {[...Array(windowWidth < 640 ? 4 : 8)].map((_, i) => {
                const particleCount = windowWidth < 640 ? 4 : 8;
                const distance =
                  windowWidth < 640
                    ? 150
                    : windowWidth < 768
                    ? 200
                    : windowWidth < 1024
                    ? 250
                    : 300;

                return (
                  <motion.div
                    key={i}
                    className="absolute top-1/2 left-1/2 w-1 h-1 bg-red-400 rounded-full sm:w-2 sm:h-2"
                    animate={{
                      x: [
                        0,
                        Math.cos((i * Math.PI) / (particleCount / 2)) *
                          distance,
                      ],
                      y: [
                        0,
                        Math.sin((i * Math.PI) / (particleCount / 2)) *
                          distance,
                      ],
                      scale: [1, 0.5, 0],
                      opacity: [1, 0.8, 0],
                    }}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                      delay: i * 0.3,
                      ease: "easeOut",
                    }}
                  />
                );
              })}

              {/* Floating Stats */}
              <motion.div
                initial={{ scale: 0, opacity: 0 }}
                whileInView={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.8, delay: 1 }}
                className="absolute -top-2 -right-2 sm:-top-4 sm:-right-4"
              >
                <div className="flex items-center px-2 py-1 text-xs text-white bg-red-500 rounded-full shadow-lg sm:px-3 sm:py-2 sm:text-sm">
                  <CheckCircle className="mr-1 w-3 h-3 sm:mr-2 sm:w-4 sm:h-4" />
                  <span className="hidden sm:inline">99.9% Success</span>
                  <span className="sm:hidden">99.9%</span>
                </div>
              </motion.div>

              <motion.div
                initial={{ scale: 0, opacity: 0 }}
                whileInView={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.8, delay: 1.2 }}
                className="absolute -bottom-2 -left-2 sm:-bottom-4 sm:-left-4"
              >
                <div className="flex items-center px-2 py-1 text-xs text-white bg-blue-500 rounded-full shadow-lg sm:px-3 sm:py-2 sm:text-sm">
                  <Users className="mr-1 w-3 h-3 sm:mr-2 sm:w-4 sm:h-4" />
                  <span className="hidden sm:inline">10K+ Recipients</span>
                  <span className="sm:hidden">10K+</span>
                </div>
              </motion.div>

              <motion.div
                initial={{ scale: 0, opacity: 0 }}
                whileInView={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.8, delay: 1.4 }}
                className="absolute left-2 top-4 sm:left-4 sm:top-10"
              >
                <div className="flex items-center px-2 py-1 text-xs text-white bg-purple-500 rounded-full shadow-lg sm:px-3 sm:py-2 sm:text-sm">
                  <Zap className="mr-1 w-3 h-3 sm:mr-2 sm:w-4 sm:h-4" />
                  <span className="hidden sm:inline">Instant Delivery</span>
                  <span className="sm:hidden">Instant</span>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default BulkSmsSection;
